// Files waiting to be sent. Each one is kept on the device with the message it belongs to, so
// sending carries on after the app is closed or crashes. Two upload at a time, in the order they
// were added, and a chat's messages are sent in that order too, so an album arrives in one piece.

import { ApiError, isPlanLimit } from "./api";
import { type ByteSource, blobSource, CHUNKED_FROM_BYTES } from "./chunked";
import type { AlbumMark, Content, MediaRef } from "./content";
import { type FileMeta, MAX_MEDIA_BYTES, type MediaClient, MediaTooLargeError } from "./media";
import { type ChunkedUploader, type ChunkedUploadState, UploadCancelled, UploadGone } from "./multipart";
import type { Store } from "./storage";

export type UploadStatus = "queued" | "uploading" | "sending" | "failed";

/** One file waiting to be sent, as it is saved on the device. */
export type QueuedUpload = {
  /** Also the client id of the message shown while it sends. */
  id: string;
  chatId: string;
  threadRoot?: string;
  kind: "photo" | "video" | "file" | "voice";
  meta: FileMeta;
  /** Plain size of the file. */
  size: number;
  caption?: string;
  reply_to?: string;
  album?: AlbumMark;
  /** People @mentioned in the caption. */
  mentions?: string[];
  purpose?: "import";
  /** Position in the queue. Lower goes first. */
  order: number;
  createdAt: string;
  status: UploadStatus;
  error?: string;
  /** A reason the screen can act on, such as "storage_full". */
  errorCode?: string;
  /** Tries since the last success or manual retry. */
  attempts: number;
  /** Set once a chunked upload has a storage key, so it can resume. */
  chunked?: ChunkedUploadState;
  /** Set once storage has the whole file. A restart then only sends the message. */
  ref?: MediaRef;
  /** Whether the file itself was saved on the device, so it survives a restart. */
  persisted: boolean;
};

/** Where queued files are kept on the device. */
export interface FileSpool {
  /** Saves the file. Resolves false when the device has no room or cannot keep it. */
  put(id: string, file: Blob): Promise<boolean>;
  open(id: string): Promise<ByteSource | null>;
  delete(id: string): Promise<void>;
  /** Ids of every saved file, so ones the queue no longer knows can be removed. */
  ids(): Promise<string[]>;
}

/** A spool that keeps nothing past this session. */
export class MemorySpool implements FileSpool {
  readonly files = new Map<string, Blob>();
  async put(id: string, file: Blob) {
    this.files.set(id, file);
    return true;
  }
  async open(id: string) {
    const file = this.files.get(id);
    return file ? blobSource(file) : null;
  }
  async delete(id: string) {
    this.files.delete(id);
  }
  async ids() {
    return [...this.files.keys()];
  }
}

export type UploadContext = {
  signal: AbortSignal;
  onProgress: (fraction: number) => void;
  /** Saves resume information for a chunked upload. */
  onState: (state: ChunkedUploadState) => Promise<void>;
};

/** What the queue asks of the app: store the file, send the message, and clean up. */
export interface UploadRunner {
  upload(item: QueuedUpload, source: ByteSource, ctx: UploadContext): Promise<MediaRef>;
  /** Sends the message for a stored file. The client's outbox takes it from here. */
  deliver(item: QueuedUpload, ref: MediaRef): Promise<void>;
  /** Throws away what storage holds for a cancelled upload. */
  discard?(item: QueuedUpload): Promise<void>;
}

export type UploadInput = {
  chatId: string;
  file: Blob;
  kind: QueuedUpload["kind"];
  meta?: FileMeta;
  caption?: string;
  reply_to?: string;
  album?: AlbumMark;
  mentions?: string[];
  threadRoot?: string;
  purpose?: "import";
  /** The client id to use, when the screen already made one. */
  id?: string;
};

export type UploadView = QueuedUpload & {
  /** Upload progress from 0 to 1 while uploading. */
  progress?: number;
  /** The file, while this session still has it, for showing it before it is sent. */
  file?: Blob;
};

export type QueueOptions = {
  concurrency?: number;
  /** Automatic tries before an upload waits for the person to retry it. */
  autoRetries?: number;
  /** Wait before an automatic retry, in ms. */
  retryDelay?: (attempt: number) => number;
  newId?: () => string;
  now?: () => Date;
};

const PREFIX = "upload:";
const NOT_ON_DEVICE = "This file is no longer on this device. Attach it again.";

function uuid(): string {
  return crypto.randomUUID();
}

/** Content of the message shown while a file sends, and sent once storage has it. */
export function uploadContent(item: QueuedUpload, ref?: MediaRef): Extract<Content, { type: "media" }> {
  const media: MediaRef = ref ?? {
    ...item.meta,
    key: "",
    file_key: "",
    iv: "",
    sha256: "",
    mime: item.meta.mime || "application/octet-stream",
    size: item.size,
  };
  return {
    type: "media",
    kind: item.kind,
    media,
    ...(item.caption ? { caption: item.caption } : {}),
    ...(item.reply_to ? { reply_to: item.reply_to } : {}),
    ...(item.album ? { album: item.album } : {}),
    ...(item.mentions?.length ? { mentions: item.mentions } : {}),
  };
}

export class UploadQueue {
  private readonly items = new Map<string, QueuedUpload>();
  /** Files added this session. Used before the spool, which may still be copying. */
  private readonly memory = new Map<string, Blob>();
  private readonly progress = new Map<string, number>();
  private readonly running = new Map<string, AbortController>();
  private readonly delivering = new Set<string>();
  private readonly waitUntil = new Map<string, number>();
  private readonly listeners = new Set<(items: UploadView[]) => void>();
  private readonly concurrency: number;
  private readonly autoRetries: number;
  private readonly retryDelay: (attempt: number) => number;
  private readonly newId: () => string;
  private readonly now: () => Date;
  private order = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private started = false;
  private stopped = false;
  private idle: (() => void)[] = [];

  constructor(
    private readonly store: Store,
    private readonly spool: FileSpool,
    private readonly runner: UploadRunner,
    opts: QueueOptions = {},
  ) {
    this.concurrency = Math.max(1, opts.concurrency ?? 2);
    this.autoRetries = Math.max(1, opts.autoRetries ?? 3);
    this.retryDelay = opts.retryDelay ?? ((n) => Math.min(60_000, 2000 * 2 ** n));
    this.newId = opts.newId ?? uuid;
    this.now = opts.now ?? (() => new Date());
  }

  /** Loads what was waiting when the app last closed and carries on with it. */
  async start(): Promise<void> {
    if (this.started) return;
    this.started = true;
    const keys = await this.store.keys(PREFIX);
    const saved = (await Promise.all(keys.map((k) => this.store.get<QueuedUpload>(k)))).filter(
      (x): x is QueuedUpload => Boolean(x),
    );
    const spooled = new Set(await this.spool.ids().catch(() => [] as string[]));
    for (const item of saved.sort((a, b) => a.order - b.order)) {
      this.order = Math.max(this.order, item.order + 1);
      if (this.items.has(item.id)) continue;
      // Anything cut off mid-way starts again, from its last stored part.
      if (item.status === "uploading" || item.status === "sending")
        item.status = item.ref ? "sending" : "queued";
      if (!item.ref && !spooled.has(item.id) && !this.memory.has(item.id)) {
        item.status = "failed";
        item.error = NOT_ON_DEVICE;
        item.errorCode = "missing_file";
      }
      this.items.set(item.id, item);
      await this.save(item);
    }
    // Files the queue no longer knows, such as one whose message was sent just before a crash.
    for (const id of spooled) if (!this.items.has(id)) await this.spool.delete(id).catch(() => undefined);
    this.emit();
    this.pump();
  }

  /** Stops starting new work. Uploads in flight are left to finish or fail. */
  stop(): void {
    this.stopped = true;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }

  /** Adds a file. It shows at once and starts uploading when its turn comes. */
  async add(input: UploadInput): Promise<QueuedUpload> {
    if (input.file.size > MAX_MEDIA_BYTES) throw new MediaTooLargeError(input.file.size);
    const meta: FileMeta = {
      ...input.meta,
      mime: input.meta?.mime || input.file.type || "application/octet-stream",
    };
    const name = input.meta?.name ?? (input.file as File).name;
    if (name) meta.name = name;
    const item: QueuedUpload = {
      id: input.id ?? this.newId(),
      chatId: input.chatId,
      kind: input.kind,
      meta,
      size: input.file.size,
      order: this.order++,
      createdAt: this.now().toISOString(),
      status: "queued",
      attempts: 0,
      persisted: false,
      ...(input.threadRoot ? { threadRoot: input.threadRoot } : {}),
      ...(input.caption ? { caption: input.caption } : {}),
      ...(input.reply_to ? { reply_to: input.reply_to } : {}),
      ...(input.album ? { album: input.album } : {}),
      ...(input.mentions?.length ? { mentions: input.mentions } : {}),
      ...(input.purpose ? { purpose: input.purpose } : {}),
    };
    this.memory.set(item.id, input.file);
    this.items.set(item.id, item);
    await this.save(item);
    this.emit();
    this.pump();
    // The copy to the device runs beside the upload, which reads the file this session holds.
    void this.spool
      .put(item.id, input.file)
      .catch(() => false)
      .then(async (ok) => {
        const current = this.items.get(item.id);
        if (!ok) {
          if (current) await this.spool.delete(item.id).catch(() => undefined);
          return;
        }
        if (!current) {
          // Sent or cancelled while it was being copied.
          await this.spool.delete(item.id).catch(() => undefined);
          return;
        }
        current.persisted = true;
        await this.save(current);
      });
    return item;
  }

  /** Stops an upload and forgets it. Its message goes away and stored parts are thrown away. */
  async cancel(id: string): Promise<void> {
    const item = this.items.get(id);
    if (!item || this.delivering.has(id)) return;
    this.running.get(id)?.abort();
    this.running.delete(id);
    await this.forget(item);
    if (item.chunked && !item.ref) await this.runner.discard?.(item)?.catch(() => undefined);
    this.emit();
    this.pump();
  }

  /** Tries a failed upload again. */
  async retry(id: string): Promise<void> {
    const item = this.items.get(id);
    if (item?.status !== "failed") return;
    if (!item.ref && !this.memory.has(id) && !(await this.spool.open(id))) {
      item.error = NOT_ON_DEVICE;
      item.errorCode = "missing_file";
      this.emit();
      return;
    }
    item.status = item.ref ? "sending" : "queued";
    item.attempts = 0;
    delete item.error;
    delete item.errorCode;
    this.waitUntil.delete(id);
    await this.save(item);
    this.emit();
    this.pump();
  }

  /** Everything waiting, in queue order. */
  list(): UploadView[] {
    return [...this.items.values()]
      .sort((a, b) => a.order - b.order)
      .map((item) => {
        const view: UploadView = { ...item };
        const p = this.progress.get(item.id);
        if (p !== undefined && item.status === "uploading") view.progress = p;
        const file = this.memory.get(item.id);
        if (file) view.file = file;
        return view;
      });
  }

  get(id: string): UploadView | undefined {
    return this.list().find((i) => i.id === id);
  }

  subscribe(listener: (items: UploadView[]) => void): () => void {
    this.listeners.add(listener);
    listener(this.list());
    return () => this.listeners.delete(listener);
  }

  /** Resolves once nothing is uploading or waiting to send. Failed items do not count. */
  whenIdle(): Promise<void> {
    if (this.isIdle()) return Promise.resolve();
    return new Promise((resolve) => this.idle.push(resolve));
  }

  private isIdle(): boolean {
    return [...this.items.values()].every((i) => i.status === "failed") && this.running.size === 0;
  }

  private emit(): void {
    const items = this.list();
    for (const l of this.listeners) l(items);
    if (this.isIdle()) {
      const waiting = this.idle;
      this.idle = [];
      for (const r of waiting) r();
    }
  }

  private async save(item: QueuedUpload): Promise<void> {
    if (!this.items.has(item.id)) return;
    await this.store.set(PREFIX + item.id, item);
  }

  private async forget(item: QueuedUpload): Promise<void> {
    this.items.delete(item.id);
    this.memory.delete(item.id);
    this.progress.delete(item.id);
    this.waitUntil.delete(item.id);
    await this.store.delete(PREFIX + item.id);
    await this.spool.delete(item.id).catch(() => undefined);
  }

  private pump(): void {
    if (!this.started || this.stopped) return;
    const now = Date.now();
    let soonest = Number.POSITIVE_INFINITY;
    const queued = [...this.items.values()]
      .filter((i) => i.status === "queued")
      .sort((a, b) => a.order - b.order);
    for (const item of queued) {
      if (this.running.size >= this.concurrency) break;
      const after = this.waitUntil.get(item.id) ?? 0;
      if (after > now) {
        soonest = Math.min(soonest, after);
        continue;
      }
      void this.run(item);
    }
    if (this.timer) clearTimeout(this.timer);
    this.timer = Number.isFinite(soonest) ? setTimeout(() => this.pump(), soonest - now) : null;
    void this.flushDeliveries();
  }

  private async run(item: QueuedUpload): Promise<void> {
    const ctl = new AbortController();
    this.running.set(item.id, ctl);
    item.status = "uploading";
    this.progress.set(item.id, 0);
    this.emit();
    try {
      await this.save(item);
      const memory = this.memory.get(item.id);
      const source = memory ? blobSource(memory) : await this.spool.open(item.id);
      if (!source) {
        this.fail(item, NOT_ON_DEVICE, "missing_file");
        return;
      }
      const ref = await this.runner.upload(item, source, {
        signal: ctl.signal,
        onProgress: (fraction) => {
          if (ctl.signal.aborted) return;
          this.progress.set(item.id, fraction);
          this.emit();
        },
        onState: async (state) => {
          item.chunked = state;
          await this.save(item);
        },
      });
      if (ctl.signal.aborted || !this.items.has(item.id)) return;
      item.ref = ref;
      item.status = "sending";
      item.attempts = 0;
      delete item.chunked;
      this.progress.delete(item.id);
      await this.save(item);
    } catch (err) {
      if (err instanceof UploadCancelled || ctl.signal.aborted || !this.items.has(item.id)) return;
      if (err instanceof UploadGone) {
        // The server dropped the half-finished upload. Start it again from the first part.
        delete item.chunked;
        await this.save(item);
        this.retryLater(item, err);
        return;
      }
      if (err instanceof MediaTooLargeError) {
        this.fail(item, err.message, "too_large");
        return;
      }
      if (isPlanLimit(err)) {
        this.fail(item, err.message, err.code);
        return;
      }
      if (
        err instanceof ApiError &&
        err.status >= 400 &&
        err.status < 500 &&
        err.status !== 429 &&
        err.status !== 408
      ) {
        this.fail(item, err.message);
        return;
      }
      this.retryLater(item, err);
    } finally {
      if (this.running.get(item.id) === ctl) this.running.delete(item.id);
      this.emit();
      this.pump();
    }
  }

  private retryLater(item: QueuedUpload, err: unknown): void {
    item.attempts += 1;
    if (item.attempts >= this.autoRetries) {
      this.fail(item, err instanceof Error ? err.message : "The upload did not finish. Try again.");
      return;
    }
    item.status = "queued";
    this.waitUntil.set(item.id, Date.now() + this.retryDelay(item.attempts - 1));
    void this.save(item);
  }

  private fail(item: QueuedUpload, message: string, code?: string): void {
    item.status = "failed";
    item.error = message;
    if (code) item.errorCode = code;
    this.progress.delete(item.id);
    void this.save(item);
  }

  /**
   * Sends finished uploads' messages in queue order, one chat at a time. A message waits while an
   * earlier file in the same chat is still uploading. A failed one does not hold the rest back.
   */
  private async flushDeliveries(): Promise<void> {
    const blocked = new Set<string>();
    for (const item of [...this.items.values()].sort((a, b) => a.order - b.order)) {
      const lane = `${item.chatId}#${item.threadRoot ?? ""}`;
      if (this.delivering.has(item.id)) {
        blocked.add(lane);
        continue;
      }
      if (item.status === "failed") continue;
      if (item.status !== "sending" || !item.ref || blocked.has(lane)) {
        blocked.add(lane);
        continue;
      }
      blocked.add(lane);
      void this.deliver(item, item.ref);
    }
  }

  private async deliver(item: QueuedUpload, ref: MediaRef): Promise<void> {
    this.delivering.add(item.id);
    try {
      await this.runner.deliver(item, ref);
      this.delivering.delete(item.id);
      await this.forget(item);
    } catch (err) {
      this.delivering.delete(item.id);
      this.fail(item, err instanceof Error ? err.message : "This message was not sent. Try again.");
    }
    this.emit();
    void this.flushDeliveries();
  }
}

/**
 * The usual runner: files from CHUNKED_FROM_BYTES go up in parts through `chunked`, smaller ones
 * as one sealed file through MediaClient.send, which every app can open.
 */
export function mediaRunner(opts: {
  media: Pick<MediaClient, "send">;
  chunked: Pick<ChunkedUploader, "upload" | "abort">;
  deliver: UploadRunner["deliver"];
  /** Size from which files are chunked. */
  chunkedFrom?: number;
}): UploadRunner {
  const from = opts.chunkedFrom ?? CHUNKED_FROM_BYTES;
  return {
    async upload(item, source, ctx) {
      if (item.size >= from || item.chunked) {
        return opts.chunked.upload({
          chatId: item.chatId,
          source,
          meta: item.meta,
          ...(item.chunked ? { state: item.chunked } : {}),
          onState: ctx.onState,
          onProgress: ctx.onProgress,
          signal: ctx.signal,
          ...(item.purpose ? { purpose: item.purpose } : {}),
        });
      }
      // Small enough to hold at once, as files always were.
      const bytes = await source.read(0, source.size);
      if (ctx.signal.aborted) throw new UploadCancelled();
      return opts.media.send(item.chatId, bytes, item.meta, ctx.onProgress, item.purpose, ctx.signal);
    },
    deliver: opts.deliver,
    async discard(item) {
      if (item.chunked) await opts.chunked.abort(item.chunked.key);
    },
  };
}
